<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { createI18nMessage, required } from '@vuelidate/validators';
import {
  BaseButton,
  FieldCheckbox,
  FieldInput,
} from '@magma-app/magma-lapilli';
import TurnstileWidget from '@/components/security/TurnstileWidget.vue';
import { useTurnstileAlert } from '@/composables/useTurnstileAlert';
import { zodEmailValidator } from '@/utils/emailValidation';
import { DEFAULT_TERMS_URL } from '@/utils/legal';

const props = defineProps<{
  initialEmail?: string;
  primaryColor?: string;
  rgbColor?: string;
  termsUrl?: string;
  widgetTitle?: string;
  helperNaming?: string;
  brandTextColor?: string;
  brandLinkColor?: string;
  brandColor?: string;
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', payload: { email: string; consent: boolean; turnstileToken?: string }): void;
}>();

const { t } = useI18n();
const withI18nMessage = createI18nMessage({ t });
const { showTurnstileErrorAlert } = useTurnstileAlert();

const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();
const isTurnstileEnabled = computed(() => Boolean(turnstileSiteKey));
const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);
const isTurnstileSubmitting = ref(false);
const isButtonLoading = computed(
  () => isTurnstileSubmitting.value || Boolean(props.isSubmitting)
);

const formData = reactive({
  consent: undefined as boolean | undefined,
  email: '',
});

const v$ = useVuelidate(
  {
    consent: {
      required: withI18nMessage((value: boolean) => value === true, {
        messagePath: () => t('validation.value'),
      }),
    },
    email: {
      email: withI18nMessage(zodEmailValidator, {
        messagePath: () => t('validation.invalidEmail'),
      }),
      required: withI18nMessage(required, {
        messagePath: () => t('validation.value'),
      }),
    },
  },
  formData
);

watch(
  () => props.initialEmail,
  (value) => {
    if (value) {
      formData.email = value;
      v$.value.email.$touch();
    }
  },
  { immediate: true }
);

watch(
  () => formData.consent,
  async () => await v$.value.consent.$validate()
);

const onValidateEmail = async () => {
  await v$.value.email.$validate();
};

const onSubmit = async () => {
  if (isTurnstileSubmitting.value) return;

  isTurnstileSubmitting.value = true;
  v$.value.$touch();
  if (!(await v$.value.$validate())) {
    isTurnstileSubmitting.value = false;
    return;
  }

  let turnstileToken: string | undefined;
  if (isTurnstileEnabled.value) {
    try {
      turnstileToken = await turnstileRef.value?.executeVerification();
      if (!turnstileToken) {
        throw new Error('TURNSTILE-TOKEN-MISSING');
      }
    } catch {
      showTurnstileErrorAlert();
      turnstileRef.value?.reset();
      isTurnstileSubmitting.value = false;
      return;
    }
  }

  emit('submit', {
    email: formData.email,
    consent: Boolean(formData.consent),
    turnstileToken,
  });

  turnstileRef.value?.reset();
  isTurnstileSubmitting.value = false;
};
</script>

<template>
  <div>
    <h2 class="text-mgm-dspl-xs font-semibold">
      <span class="block text-foreground-default">
        {{ $t('helpeeSignUp.creation.header.community.title') }}
      </span>
      <span class="block text-foreground-emphasis">
        {{ $t('helpeeSignUp.creation.header.community.subtitle') }}
        {{ helperNaming?.toLowerCase() }}
      </span>
    </h2>

    <FieldInput
      id="email"
      v-model="formData.email"
      data-cy="email"
      class="mt-6"
      role="email"
      type="email"
      :locale="$i18n.locale"
      :label="$t('generic.yourEmail')"
      :placeholder="`${$t('generic.example')}@email.com`"
      autocomplete="email"
      required
      :validation="v$.email"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      @blur="onValidateEmail"
    />

    <FieldCheckbox
      id="consent"
      v-model="formData.consent"
      data-cy="consent"
      class="mt-6"
      :custom-color="brandColor"
      :custom-rgb-color="rgbColor || primaryColor"
      :validation="v$.consent"
      required
    >
      <span class="text-mgm-txt-sm font-normal text-foreground-default">
        {{ $t('generic.agree') }}

        <a
          class="font-medium"
          :class="{
            'text-foreground-brand-default': !primaryColor
          }"
          :style="{
            color: brandLinkColor
          }"
          href="https://fr.magma.app/legals/supported-terms-of-use"
          target="_blank"
        >
          {{ $t('generic.termsAndPrivacy.part2') }}
        </a>

        {{ $t('generic.termsAndPrivacy.part3') }}

        <a
          class="font-medium"
          :class="{
            'text-foreground-brand-default': !primaryColor
          }"
          :style="{
            color: brandLinkColor
          }"
          :href="termsUrl || DEFAULT_TERMS_URL"
          target="_blank"
        >
          {{ $t('generic.termsAndPrivacy.part4') }}
        </a>
      </span>
    </FieldCheckbox>

    <TurnstileWidget
      v-if="isTurnstileEnabled"
      ref="turnstileRef"
      class="mt-4"
      :site-key="turnstileSiteKey"
      action="signup-prospect"
      execution="execute"
      appearance="interaction-only"
    />

    <BaseButton
      data-cy="signup-start"
      class="mx-auto w-full mt-8"
      :custom-color="primaryColor"
      :custom-rgb-color="rgbColor || primaryColor"
      :disabled="
        !v$.email.$dirty ||
        v$.email.$error ||
        !v$.consent.$dirty ||
        v$.consent.$error ||
        isButtonLoading
      "
      size="md"
      @click="onSubmit"
    >
      <span
        class="inline-flex items-center justify-center gap-2"
        :style="{ color: brandTextColor }"
      >
        <span
          v-if="isButtonLoading"
          class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
        <span>{{ widgetTitle }}</span>
      </span>
    </BaseButton>
  </div>
</template>
